import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchAccountsByAccountingIds } from "@/api/fetchAccountsByAccountingIds/fetchAccountsByAccountingIdsApi";

const STOCHASTIC_CORPORATION = "stochastic";

export type BillabilityStatus = "billable" | "no_account" | "no_payment_method";

interface AccountBillabilityState {
  billability: Partial<Record<string, BillabilityStatus>>;
  loading: boolean;
  error: string | null;
}

const initialState: AccountBillabilityState = {
  billability: {},
  loading: false,
  error: null,
};

const accountBillabilitySlice = createSlice({
  name: "accountBillability",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setBillability: (
      state,
      action: PayloadAction<Partial<Record<string, BillabilityStatus>>>,
    ) => {
      Object.assign(state.billability, action.payload);
    },
  },
});

export const { setLoading, setError, setBillability } =
  accountBillabilitySlice.actions;

export const fetchAccountBillabilityAction =
  (accountingIds: string[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    if (accountingIds.length === 0) return;

    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const response = await fetchAccountsByAccountingIds(
        STOCHASTIC_CORPORATION.toLowerCase(),
        accountingIds,
      );
      const billability: Partial<Record<string, BillabilityStatus>> = {};

      for (const [id, account] of Object.entries(response.data)) {
        if (account === null) {
          billability[id] = "no_account";
        } else if (
          account.preferredPaySources?.[STOCHASTIC_CORPORATION] == null
        ) {
          billability[id] = "no_payment_method";
        } else {
          billability[id] = "billable";
        }
      }

      dispatch(setBillability(billability));
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch account billability",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export default accountBillabilitySlice.reducer;
